import React, { useCallback, useEffect, useMemo, useState } from 'react';
import particularesApiService from '../../services/ParticularesApiService';
import TableExportActions from '../common/TableExportActions';
import {
  ESTADOS_EDITABLES,
  formatFechaHoraUtc,
  formatRestante,
  getEstadoAsistencia,
  normalizeAsistencia,
  porcentajeAsistencia
} from './asistenciasUtils';
import './Asistencias.css';

// Toma de asistencia por año/división ("Modo colegio"): administrador, admin general, preceptor de la división
// o profesor que dicta algún curso de la división. Muestra el código grande con la cuenta regresiva, el avance
// en vivo, el historial y el detalle por alumno (con corrección manual y descarga en Excel/PDF).

const DURACIONES = [5, 10, 15, 20, 30, 45, 60];
const POLL_MS = 5000;

const getAlumnoNombre = (alumno) => {
  const apellido = alumno?.apellido || alumno?.Apellido;
  const nombre = alumno?.nombre || alumno?.Nombre;
  if (apellido || nombre) return [apellido, nombre].filter(Boolean).join(', ');
  return alumno?.displayName || alumno?.DisplayName || alumno?.email || alumno?.Email || 'Alumno';
};

const AsistenciasDivision = () => {
  const [divisiones, setDivisiones] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [asistencias, setAsistencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [crear, setCrear] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    particularesApiService.getAsistenciaDivisiones()
      .then((response) => {
        if (!active) return;
        const items = (response?.divisiones || response?.Divisiones || []).map((d) => ({
          id: String(d.id || d.Id),
          nombreCompleto: d.nombreCompleto || d.NombreCompleto || 'División'
        }));
        setDivisiones(items);
        setDivisionId((prev) => prev || items[0]?.id || '');
        setError('');
      })
      .catch((err) => active && setError(err?.message || 'No pudimos cargar las divisiones.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const loadAsistencias = useCallback(async (silent = false) => {
    if (!divisionId) return;
    if (!silent) setLoading(true);
    try {
      const response = await particularesApiService.getAsistencias(divisionId);
      setAsistencias((response?.asistencias || response?.Asistencias || []).map(normalizeAsistencia));
      setError('');
    } catch (err) {
      if (!silent) setError(err?.message || 'No pudimos cargar las asistencias.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [divisionId]);

  useEffect(() => {
    loadAsistencias();
  }, [loadAsistencias]);

  const abierta = useMemo(() => asistencias.find((a) => a.abierta), [asistencias]);

  // Mientras hay una abierta: reloj cada segundo y avance cada 5 s.
  useEffect(() => {
    if (!abierta) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => loadAsistencias(true), POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [abierta, loadAsistencias]);

  useEffect(() => {
    if (abierta && new Date(abierta.venceUtc).getTime() <= now) {
      loadAsistencias(true);
    }
  }, [abierta, now, loadAsistencias]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await particularesApiService.createAsistencia({
        divisionId,
        titulo: crear.titulo.trim() || null,
        duracionMinutos: Number(crear.duracion)
      });
      setCrear(null);
      setNow(Date.now());
      await loadAsistencias(true);
    } catch (err) {
      setError(err?.message || 'No pudimos crear la toma de asistencia.');
    } finally {
      setSaving(false);
    }
  };

  const handleCerrar = async (id) => {
    setSaving(true);
    try {
      await particularesApiService.cerrarAsistencia(id);
      await loadAsistencias(true);
      if (detalle?.asistencia?.id === id) await openDetalle(id);
    } catch (err) {
      setError(err?.message || 'No pudimos cerrar la asistencia.');
    } finally {
      setSaving(false);
    }
  };

  const openDetalle = async (id) => {
    try {
      const response = await particularesApiService.getAsistencia(id);
      setDetalle({
        asistencia: normalizeAsistencia(response?.asistencia || response?.Asistencia),
        alumnos: (response?.alumnos || response?.Alumnos || []).map((a) => ({
          idAlumno: String(a.idAlumno || a.IdAlumno),
          nombre: getAlumnoNombre(a),
          email: a.email || a.Email || '',
          estado: a.estado || a.Estado || 'pendiente',
          fechaRegistroUtc: a.fechaRegistroUtc || a.FechaRegistroUtc || '',
          porCodigo: Boolean(a.registradaPorCodigo ?? a.RegistradaPorCodigo)
        })),
        error: ''
      });
    } catch (err) {
      setError(err?.message || 'No pudimos cargar el detalle.');
    }
  };

  const cambiarEstado = async (alumno, estado) => {
    const id = detalle.asistencia.id;
    setDetalle((prev) => ({ ...prev, alumnos: prev.alumnos.map((a) => (a.idAlumno === alumno.idAlumno ? { ...a, estado, porCodigo: false } : a)), error: '' }));
    try {
      await particularesApiService.setAsistenciaAlumno(id, alumno.idAlumno, estado);
      loadAsistencias(true);
    } catch (err) {
      setDetalle((prev) => ({ ...prev, alumnos: prev.alumnos.map((a) => (a.idAlumno === alumno.idAlumno ? alumno : a)), error: err?.message || 'No se pudo guardar.' }));
    }
  };

  const divisionNombre = divisiones.find((d) => d.id === divisionId)?.nombreCompleto || '';

  return (
    <section className="as-panel">
      <header className="as-header">
        <div>
          <h2><i className="fas fa-clipboard-check" aria-hidden="true" /> Asistencias</h2>
          <p>Tomá asistencia a un año/división: los alumnos de todos sus cursos reciben el aviso y dan el presente con el código.</p>
        </div>
        <div className="as-header-actions">
          <select
            className="as-select"
            value={divisionId}
            onChange={(event) => setDivisionId(event.target.value)}
            aria-label="División"
            disabled={divisiones.length === 0}
          >
            {divisiones.length === 0 && <option value="">No tenés divisiones a cargo</option>}
            {divisiones.map((division) => <option key={division.id} value={division.id}>{division.nombreCompleto}</option>)}
          </select>
          <button
            type="button"
            className="as-btn as-btn-primary"
            onClick={() => setCrear({ titulo: '', duracion: 10 })}
            disabled={!divisionId || Boolean(abierta)}
            title={abierta ? 'Ya hay una toma abierta en esta división' : 'Tomar asistencia'}
          >
            <i className="fas fa-bullhorn" aria-hidden="true" />
            Tomar asistencia
          </button>
        </div>
      </header>

      {error && <p className="as-error" role="alert">{error}</p>}

      {abierta && (
        <div className="as-live">
          <div className="as-live-code">
            <span>Código para {abierta.divisionNombre}{abierta.titulo ? ` · ${abierta.titulo}` : ''}</span>
            <strong>{abierta.codigo ? `${abierta.codigo.slice(0, 3)} ${abierta.codigo.slice(3)}` : '------'}</strong>
            <small><i className="fas fa-hourglass-half" aria-hidden="true" /> Cierra en {formatRestante(abierta.venceUtc, now)}</small>
          </div>
          <div className="as-live-stats">
            <div className="as-live-progress">
              <span style={{ width: `${abierta.total ? Math.round(((abierta.total - abierta.pendientes) * 100) / abierta.total) : 0}%` }} />
            </div>
            <p><b>{abierta.presentes + abierta.tardes}</b> de {abierta.total} dieron el presente · {abierta.pendientes} pendientes</p>
            <div className="as-live-actions">
              <button type="button" className="as-btn" onClick={() => openDetalle(abierta.id)}>
                <i className="fas fa-users" aria-hidden="true" />
                Ver alumnos
              </button>
              <button type="button" className="as-btn as-btn-danger" onClick={() => handleCerrar(abierta.id)} disabled={saving}>
                <i className="fas fa-lock" aria-hidden="true" />
                Cerrar ahora
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="as-section-title">
        <h3>Historial{divisionNombre ? ` de ${divisionNombre}` : ''}</h3>
        <TableExportActions
          columns={[
            { key: 'inicioUtc', title: 'Fecha', exportValue: (v) => formatFechaHoraUtc(v) },
            { key: 'titulo', title: 'Título', exportValue: (v) => v || '-' },
            { key: 'creadaPor', title: 'Tomada por' },
            { key: 'total', title: 'Alumnos' },
            { key: 'presentes', title: 'Presentes' },
            { key: 'tardes', title: 'Tarde' },
            { key: 'ausentes', title: 'Ausentes' },
            { key: 'justificados', title: 'Justificados' },
            { key: 'id', title: '% asistencia', exportValue: (_, row) => (porcentajeAsistencia(row) == null ? '-' : `${porcentajeAsistencia(row)}%`) }
          ]}
          rows={asistencias}
          fileName={`asistencias-${divisionNombre || 'division'}`}
          title={`Asistencias - ${divisionNombre}`}
          compact
          disabled={asistencias.length === 0}
        />
      </div>

      <div className="as-table-wrap">
        <table className="as-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Título</th>
              <th>Estado</th>
              <th className="num">Presentes</th>
              <th className="num">Ausentes</th>
              <th className="num">% asistencia</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {asistencias.map((asistencia) => (
              <tr key={asistencia.id}>
                <td>
                  {formatFechaHoraUtc(asistencia.inicioUtc)}
                  {asistencia.creadaPor && <small className="as-sub">por {asistencia.creadaPor}</small>}
                </td>
                <td>{asistencia.titulo || <span className="as-muted">-</span>}</td>
                <td>{asistencia.abierta ? <span className="as-chip as-chip-live">Abierta</span> : <span className="as-chip as-chip-muted">Cerrada</span>}</td>
                <td className="num">{asistencia.presentes + asistencia.tardes}/{asistencia.total}</td>
                <td className="num">{asistencia.ausentes}</td>
                <td className="num">{porcentajeAsistencia(asistencia) == null ? '—' : `${porcentajeAsistencia(asistencia)}%`}</td>
                <td className="num">
                  <button type="button" className="as-btn as-btn-sm" onClick={() => openDetalle(asistencia.id)}>Ver</button>
                </td>
              </tr>
            ))}
            {!loading && asistencias.length === 0 && (
              <tr><td colSpan={7} className="as-empty">{divisionId ? 'Todavía no se tomó asistencia en esta división.' : 'Elegí una división.'}</td></tr>
            )}
            {loading && <tr><td colSpan={7} className="as-empty"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Cargando...</td></tr>}
          </tbody>
        </table>
      </div>

      {crear && (
        <div className="as-overlay" onMouseDown={saving ? undefined : () => setCrear(null)}>
          <form className="as-modal" onSubmit={handleCreate} onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <span className="as-eyebrow">Tomar asistencia</span>
                <h3>{divisionNombre}</h3>
              </div>
            </header>
            <div className="as-modal-body">
              <label>
                Título <small>(opcional)</small>
                <input value={crear.titulo} onChange={(event) => setCrear((prev) => ({ ...prev, titulo: event.target.value }))} placeholder='Ej.: "Ingreso", "Después del recreo"' maxLength={120} />
              </label>
              <label>
                El código vence en
                <select value={crear.duracion} onChange={(event) => setCrear((prev) => ({ ...prev, duracion: event.target.value }))}>
                  {DURACIONES.map((minutos) => <option key={minutos} value={minutos}>{minutos} minutos</option>)}
                </select>
              </label>
              <p className="as-muted">Los alumnos de todos los cursos de la división reciben una notificación en el celular. Al cerrar, los que no dieron el presente quedan ausentes (después podés corregirlo).</p>
            </div>
            <footer>
              <button type="button" className="as-btn" onClick={() => setCrear(null)} disabled={saving}>Cancelar</button>
              <button type="submit" className="as-btn as-btn-primary" disabled={saving}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-bullhorn'}`} aria-hidden="true" />
                Generar código y avisar
              </button>
            </footer>
          </form>
        </div>
      )}

      {detalle && (
        <div className="as-overlay" onMouseDown={() => setDetalle(null)}>
          <div className="as-modal as-modal-wide" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <span className="as-eyebrow">{detalle.asistencia.divisionNombre}</span>
                <h3>{formatFechaHoraUtc(detalle.asistencia.inicioUtc)}{detalle.asistencia.titulo ? ` · ${detalle.asistencia.titulo}` : ''}</h3>
              </div>
              <div className="as-modal-head-actions">
                <TableExportActions
                  columns={[
                    { key: 'nombre', title: 'Alumno' },
                    { key: 'email', title: 'Email' },
                    { key: 'estado', title: 'Estado', exportValue: (v) => getEstadoAsistencia(v).label },
                    { key: 'fechaRegistroUtc', title: 'Registro', exportValue: (v) => formatFechaHoraUtc(v) || '-' },
                    { key: 'porCodigo', title: 'Con código', exportValue: (v) => (v ? 'Sí' : 'No') }
                  ]}
                  rows={detalle.alumnos}
                  fileName={`asistencia-${detalle.asistencia.divisionNombre}`}
                  title={`Asistencia ${detalle.asistencia.divisionNombre} - ${formatFechaHoraUtc(detalle.asistencia.inicioUtc)}`}
                  compact
                />
                <button type="button" className="as-icon-btn" onClick={() => setDetalle(null)} title="Cerrar">
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            </header>
            <div className="as-modal-body">
              {detalle.error && <p className="as-error">{detalle.error}</p>}
              <div className="as-table-wrap">
                <table className="as-table">
                  <thead>
                    <tr>
                      <th>Alumno</th>
                      <th>Registro</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.alumnos.map((alumno) => (
                      <tr key={alumno.idAlumno}>
                        <td>
                          <strong>{alumno.nombre}</strong>
                          {alumno.email && <small className="as-sub">{alumno.email}</small>}
                        </td>
                        <td>
                          {alumno.fechaRegistroUtc ? formatFechaHoraUtc(alumno.fechaRegistroUtc) : <span className="as-muted">-</span>}
                          {alumno.porCodigo && <small className="as-sub">con código</small>}
                        </td>
                        <td>
                          <select
                            className={`as-estado-select as-chip-${getEstadoAsistencia(alumno.estado).tone}`}
                            value={alumno.estado}
                            onChange={(event) => cambiarEstado(alumno, event.target.value)}
                            aria-label={`Estado de ${alumno.nombre}`}
                          >
                            {alumno.estado === 'pendiente' && <option value="pendiente">Pendiente</option>}
                            {ESTADOS_EDITABLES.map((estado) => <option key={estado} value={estado}>{getEstadoAsistencia(estado).label}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detalle.asistencia.abierta && (
                <p className="as-muted">La toma sigue abierta: los pendientes todavía pueden dar el presente con el código.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AsistenciasDivision;
